import { useMediaQuery, useTheme } from "@mui/material";

/**
 * "Is this a phone-or-tablet sized viewport?" — the `md` boundary the app shell already uses to
 * swap the SideNav for a temporary drawer (`layouts/DashboardLayout`), so a page that branches on
 * this branches WITH the chrome around it rather than against it.
 *
 * ── prefer `sx` ──
 *
 * Reach for a responsive `sx` value (`{ xs: a, md: b }`) whenever the difference is only a STYLE.
 * That keeps the breakpoint in the CSS, where a resize, a print and a test all see the same thing,
 * and it costs no re-render. This hook is for the cases where the difference is STRUCTURAL — a
 * different component, a different number of DOM nodes, an element that has to live somewhere else
 * in the tree. Rendering both and hiding one with `display: none` is not a substitute: it puts two
 * copies of every label and every `role` in the accessibility tree.
 *
 * ── `noSsr` ──
 *
 * Without it MUI returns the default (`false`, i.e. desktop) on the first render and corrects on the
 * second, which on a phone is a visible flash of the desktop layout. `noSsr` reads `matchMedia`
 * synchronously during that first render. This app has no SSR, so there is nothing to lose by it.
 *
 * ── in tests ──
 *
 * jsdom ships no `matchMedia`, and MUI answers `defaultMatches` (`false` for this query) when it is
 * absent — so a test that says nothing about the viewport gets the desktop branch, which is what
 * the existing suite has always asserted. A test that wants the phone calls `setViewportWidth(375)`
 * from `test-utils/matchMedia`.
 *
 * `useTheme()` rather than `useMediaQuery`'s theme-callback form: the callback reads the theme from
 * context and gets `null` when there is no `ThemeProvider`, which throws. Hundreds of component
 * tests render bare, and a hook a page uses must not be the reason one of them crashes. `useTheme`
 * falls back to MUI's default theme — where `md` is 900 either way, since the app does not override
 * its breakpoints.
 */
export const useIsMobile = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
};

export default useIsMobile;
